/**
 * Projection typography (2026-08-09): the moderation can restyle the beamer
 * text live — font from a curated 20-face list plus a size factor. The venue
 * has no internet (styles.css header), so every face is either bundled
 * (Anton) or a system-font stack with cross-platform fallbacks; nothing is
 * fetched at show time.
 */

import type { DisplaySettings } from './types';

export interface BeamFont {
  id: string;
  /** Button label, rendered in the face itself. */
  label: string;
  /** Short German hint for the moderation (tooltip). */
  hint: string;
  /** CSS font-family stack. */
  stack: string;
}

export const BEAM_FONTS: BeamFont[] = [
  { id: 'anton', label: 'Anton', hint: 'Bühnen-Standard, kondensiert', stack: "'Anton', 'Arial Narrow', sans-serif" },
  { id: 'helvetica', label: 'Helvetica', hint: 'Neutrale Grotesk', stack: "'Helvetica Neue', Helvetica, Arial, sans-serif" },
  { id: 'futura', label: 'Futura', hint: 'Geometrisch, Bauhaus', stack: "Futura, 'Century Gothic', 'Trebuchet MS', sans-serif" },
  { id: 'avenir', label: 'Avenir', hint: 'Humanistische Sans', stack: "'Avenir Next', Avenir, 'Segoe UI', sans-serif" },
  { id: 'gill', label: 'Gill Sans', hint: 'Britische Sans', stack: "'Gill Sans', 'Gill Sans MT', Calibri, sans-serif" },
  { id: 'optima', label: 'Optima', hint: 'Elegante Sans mit Serifen-Anklang', stack: "Optima, Candara, 'Segoe UI', sans-serif" },
  { id: 'georgia', label: 'Georgia', hint: 'Robuste Bildschirm-Serife', stack: "Georgia, 'Times New Roman', serif" },
  { id: 'palatino', label: 'Palatino', hint: 'Klassische Buch-Serife', stack: "'Palatino Linotype', Palatino, 'Book Antiqua', serif" },
  { id: 'baskerville', label: 'Baskerville', hint: 'Feine Barock-Serife', stack: "Baskerville, 'Baskerville Old Face', Georgia, serif" },
  { id: 'hoefler', label: 'Hoefler Text', hint: 'Literarische Serife', stack: "'Hoefler Text', 'Iowan Old Style', Garamond, serif" },
  { id: 'didot', label: 'Didot', hint: 'Mondäne Modeserife', stack: "Didot, 'Bodoni MT', Georgia, serif" },
  { id: 'typewriter', label: 'Typewriter', hint: 'Schreibmaschine', stack: "'American Typewriter', 'Courier New', monospace" },
  { id: 'mono', label: 'Menlo Mono', hint: 'Nüchterne Monospace', stack: "Menlo, Consolas, 'Courier New', monospace" },
  { id: 'impact', label: 'Impact', hint: 'Fette Plakatschrift', stack: "Impact, 'Arial Black', 'Helvetica Neue', sans-serif" },
  { id: 'copperplate', label: 'Copperplate', hint: 'Gravierte Kapitälchen', stack: "Copperplate, 'Copperplate Gothic Bold', fantasy" },
  { id: 'marker', label: 'Marker Felt', hint: 'Filzstift, handschriftlich', stack: "'Marker Felt', 'Comic Sans MS', cursive" },
  { id: 'chalk', label: 'Chalkduster', hint: 'Kreide auf Tafel', stack: "Chalkduster, 'Segoe Print', 'Comic Sans MS', cursive" },
  { id: 'brush', label: 'Brush Script', hint: 'Schwungvoller Pinsel', stack: "'Brush Script MT', 'Segoe Script', cursive" },
  { id: 'snell', label: 'Snell Roundhand', hint: 'Kalligrafie, festlich', stack: "'Snell Roundhand', 'Apple Chancery', 'Edwardian Script ITC', cursive" },
  { id: 'luminari', label: 'Luminari', hint: 'Fantasy, Märchenton', stack: "Luminari, Papyrus, fantasy" },
];

export const MIN_FONT_SCALE = 0.5;
export const MAX_FONT_SCALE = 2.5;

export const DEFAULT_DISPLAY: DisplaySettings = { fontId: 'anton', fontScale: 1 };

export function beamFont(id: string): BeamFont {
  return BEAM_FONTS.find((f) => f.id === id) ?? BEAM_FONTS[0]!;
}
